import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BookOpen, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { languageDisplayName } from "@/lib/languageNames";
import { VocabularyCaptureOutcomeEnum } from "@/__generated__/graphql";
import { captureErrorKey } from "@/lib/flashcards";
import { MAX_FLASHCARD_TEXT_LENGTH, removeFlashcardCapture } from "@/lib/live/flashcard-capture";
import { invalidateAfterCapture } from "@/lib/vocabulary-entries";
import { selectionText, tokenizeForCapture, type TokenRange } from "@/lib/vocabulary-tokenize";
import type { CaptureLanguage } from "./captureLanguages";
import { SAVE_VOCABULARY_ENTRY } from "./queries";
import { useStudentL1Status } from "@/lib/languages";
import NativeLanguagePrompt from "@/components/flashcards/NativeLanguagePrompt";

interface Props {
  messageId: string;
  body: string;
  open: boolean;
  onClose: () => void;
  // Target languages on offer (see captureLanguages.ts) and the preselected
  // one. The active language is also the segmentation hint for the tokenizer.
  languages: CaptureLanguage[];
  defaultLanguageCode: string | null;
  // "tutor" switches every line of copy to the "for ‹student›" variants: the
  // card lands in the student's deck, so "your flashcards" would be a lie.
  viewerRole?: string;
  studentFirstName?: string | null;
}

// Mirrors the classroom WordPopover: long enough to read and hit Undo, well
// inside the server's 60-second cancellation window.
const UNDO_TOAST_MS = 8000;

export function VocabularyPopup({
  messageId,
  body,
  open,
  onClose,
  languages,
  defaultLanguageCode,
  viewerRole,
  studentFirstName = null,
}: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [pickedCode, setPickedCode] = useState<string | null>(null);
  // Opened when the server says it only guessed this student's L1 (#878).
  const [askNative, setAskNative] = useState(false);
  // Lazy: only fetched once the server has actually refused a capture this way.
  const { data: l1Status } = useStudentL1Status({ enabled: askNative });
  const activeLanguage =
    languages.find((l) => l.code === (pickedCode ?? defaultLanguageCode)) ?? languages[0] ?? null;
  const activeCode = activeLanguage?.code ?? null;
  const tokens = useMemo(() => tokenizeForCapture(body, activeCode), [body, activeCode]);
  const [dragStart, setDragStart] = useState<number | null>(null);
  const [dragEnd, setDragEnd] = useState<number | null>(null);
  // Whether the pointer is held right now. Crossing a word extends the range
  // ONLY while it is: without this, one click armed the range forever and
  // every word the pointer crossed on its way to Save joined the selection.
  const dragging = useRef(false);
  // Whether the current hold has reached past the word it started on. A press
  // that never moved is a tap or a click and selects that one word; a press
  // that dragged must not have its closing click shrink the range back to the
  // word it started on — which is where a finger's click lands, since a touch
  // is delivered to its first target for the whole gesture.
  const dragMoved = useRef(false);
  useEffect(() => {
    if (!open) return;
    const release = () => {
      dragging.current = false;
      // The click that ends a drag is dispatched right after pointerup; let it
      // through the guard first, then forget the drag.
      window.setTimeout(() => {
        dragMoved.current = false;
      }, 0);
    };
    window.addEventListener("pointerup", release);
    window.addEventListener("pointercancel", release);
    return () => {
      window.removeEventListener("pointerup", release);
      window.removeEventListener("pointercancel", release);
    };
  }, [open]);
  // A modal: Escape closes it and focus starts inside it, so a keyboard user
  // is not left on the page behind the overlay.
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  // Read through a ref so a new callback identity cannot tear the effect down
  // and re-run it — that would steal focus on every render.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);
  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      // aria-modal promises the rest of the page is inert; keep Tab inside.
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = [
        ...panel.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      ];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!panel.contains(active)) {
        e.preventDefault();
        ;(e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && (active === first || active === panel)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      // Back to whatever opened it ("Save vocabulary" on the message).
      opener?.focus?.();
    };
  }, [open]);
  // The component stays mounted while closed: whatever path closed it
  // (Escape, the backdrop, Cancel), the next open starts clean.
  useEffect(() => {
    if (open) return;
    setDragStart(null);
    setDragEnd(null);
    setPickedCode(null);
  }, [open]);
  // A drag that ends over the backdrop dispatches its click to the backdrop;
  // only a press that STARTED there may close the popup.
  const backdropPressed = useRef(false);
  const [saveEntry, { loading }] = useMutation(SAVE_VOCABULARY_ENTRY);

  const isTutor = viewerRole === "tutor";
  const name = studentFirstName ?? "";

  if (!open) return null;

  const range: TokenRange =
    dragStart !== null && dragEnd !== null
      ? [Math.min(dragStart, dragEnd), Math.max(dragStart, dragEnd)]
      : null;

  const isSelected = (i: number) => range !== null && i >= range[0] && i <= range[1];

  const selectedText = selectionText(tokens, range);
  // Mirrors the server cap (VocabularyEntry::MAX_TEXT_LENGTH) so the refusal
  // is read here, not from an error toast after the round trip.
  const tooLong = selectedText.length > MAX_FLASHCARD_TEXT_LENGTH;

  const clearSelection = () => {
    setDragStart(null);
    setDragEnd(null);
  };

  // The pointer, still held, is over word `i`.
  const extendTo = (i: number) => {
    if (!dragging.current || dragStart === null) return;
    if (i !== dragStart) dragMoved.current = true;
    setDragEnd(i);
  };

  const close = () => {
    onClose();
  };

  const pickLanguage = (code: string) => {
    if (code === activeCode) return;
    setPickedCode(code);
    // Token boundaries can differ per language hint — drop a stale range.
    clearSelection();
  };

  // Undo inside the 60 s buffer: the server cancels the pending entry and its
  // GenerateFlashcardJob no-ops (a tutor's Undo is accepted only while the
  // buffer is open — the server's rule, not ours). A transport failure
  // is reported like a refusal: silence would let the student believe the
  // card was cancelled while it goes on to generate.
  //
  // A refusal says the same thing the classroom consumer of this mutation
  // says (useLessonCaptures#undo): `forbidden` is removeVocabularyHighlight's
  // can_remove? saying no — the tutor's buffer lapsed (sonner pauses the toast
  // timer while the tab is hidden, so this is reachable) — which reads as
  // "too late", never as a transient failure; anything else is the generic
  // line. Never captureErrorKey: that table decodes the CAPTURE mutation, and
  // would render `forbidden` as "you can't save a flashcard from here" while
  // the card sits in the student's deck.
  const undoCapture = (entryId: string) => {
    removeFlashcardCapture(entryId)
      .then((result) => {
        if (result.success) {
          toast.info(t("chat.vocabularyPopup.undoneToast"));
          invalidateAfterCapture(queryClient);
        } else {
          toast.error(
            t(result.errorCode === "forbidden" ? "liveSession.vocabularyStrip.undoTooLateToast" : "liveSession.vocabularyStrip.undoFailedToast"),
          );
        }
      })
      .catch(() => toast.error(t("liveSession.vocabularyStrip.undoFailedToast")));
  };

  const onSave = async () => {
    if (!selectedText || !activeLanguage) return;
    const word = selectedText;
    try {
      const result = await saveEntry({
        variables: { input: { messageId, text: word, language: activeLanguage.code } },
      });
      const payload = result.data?.saveVocabularyEntry;
      const errors = payload?.errors ?? [];
      if (errors.length > 0) {
        // Stable code → translated copy; an unknown code shows the server sentence.
        const key = captureErrorKey(payload?.errorCode);
        toast.error(key ? t(key) : errors.join(", "));
        return;
      }
      switch (payload?.outcome) {
        case VocabularyCaptureOutcomeEnum.Queued: {
          const entryId = payload.vocabularyEntry?.id;
          // Undo only on an entry this save created (`reused` = it pre-existed,
          // possibly the other participant's capture from the classroom).
          const canUndo = !!entryId && !payload.reused;
          toast.success(
            isTutor
              ? t("chat.vocabularyPopup.queuedToastTutor", { name, word })
              : t("chat.vocabularyPopup.queuedToast", { word }),
            {
              description: t("chat.vocabularyPopup.queuedDescription"),
              duration: UNDO_TOAST_MS,
              action: canUndo
                ? { label: t("chat.vocabularyPopup.undo"), onClick: () => undoCapture(entryId) }
                : undefined,
            },
          );
          break;
        }
        case VocabularyCaptureOutcomeEnum.AlreadySaved:
          toast.success(
            isTutor
              ? t("chat.vocabularyPopup.alreadySavedToastTutor", { name, word })
              : t("chat.vocabularyPopup.alreadySavedToast", { word }),
          );
          break;
        case VocabularyCaptureOutcomeEnum.SkippedNativeLanguage:
          toast.info(
            isTutor
              ? t("chat.vocabularyPopup.skippedNativeToastTutor", { name })
              : t("chat.vocabularyPopup.skippedNativeToast"),
          );
          break;
        // #878: the same collision, but nobody ever asked the student what
        // they speak — the L1 was guessed from their interface locale. A tutor
        // cannot answer for them, so they get the fact and no call to action.
        case VocabularyCaptureOutcomeEnum.NeedsNativeLanguage:
          if (isTutor) {
            toast.info(t("chat.vocabularyPopup.needsNativeToastTutor", { name }));
          } else {
            toast.info(t("chat.vocabularyPopup.needsNativeToast"));
            setAskNative(true);
          }
          break;
        case VocabularyCaptureOutcomeEnum.UnsupportedLanguage:
          toast.info(t("chat.vocabularyPopup.unsupportedLanguageToast", { language: languageDisplayName(activeLanguage.code, activeLanguage.name, i18n.language) }));
          break;
        default:
          toast.error(t("chat.vocabularyPopup.saveFailedToast"));
          return;
      }
      // The deck list, browse and the entry feeds all change on a capture
      // (even "already saved" may have surfaced a card the cache predates).
      invalidateAfterCapture(queryClient);
      close();
    } catch {
      toast.error(t("chat.vocabularyPopup.saveFailedToast"));
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/40 p-4 backdrop-blur-sm"
      onPointerDown={(e) => {
        backdropPressed.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && backdropPressed.current) close();
        backdropPressed.current = false;
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="w-full max-w-lg overflow-hidden rounded-2xl border border-border bg-popover shadow-2xl outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 border-b border-border px-5 py-4">
          <div className="flex size-9 items-center justify-center rounded-full bg-primary/15 text-primary">
            <BookOpen className="size-4" />
          </div>
          <div className="flex-1">
            <h2 id={titleId} className="font-display text-[16px] font-semibold leading-tight">
              {isTutor ? t("chat.vocabularyPopup.titleFor", { name }) : t("chat.vocabularyPopup.title")}
            </h2>
            <p className="text-[12px] text-muted-foreground">
              {isTutor ? t("chat.vocabularyPopup.descriptionTutor", { name }) : t("chat.vocabularyPopup.description")}
            </p>
          </div>
          <button
            onClick={close}
            className="inline-flex size-8 items-center justify-center rounded-full text-muted-foreground hover:bg-secondary focus-ring"
            aria-label={t("chat.vocabularyPopup.close")}
          >
            <X className="size-4" />
          </button>
        </div>

        {languages.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-5 py-3">
            <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("chat.vocabularyPopup.languageLabel")}
            </span>
            {languages.map((language) => {
              const selected = language.code === activeCode;
              return (
                <button
                  key={language.code}
                  type="button"
                  aria-pressed={selected}
                  onClick={() => pickLanguage(language.code)}
                  className={cn(
                    "rounded-full border px-3 py-1 text-[12px] font-medium transition-colors focus-ring",
                    selected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-background text-muted-foreground hover:bg-secondary",
                  )}
                >
                  {languageDisplayName(language.code, language.name, i18n.language)}
                </button>
              );
            })}
          </div>
        )}

        {/* Every word is a real button: Tab reaches it, Space/Enter selects it,
            Shift+Enter (or Shift+click) extends the selection to it. A drag
            extends it too, with a mouse or a finger. `touch-none` keeps the
            browser from taking a finger's drag for a scroll; and because a
            touch is delivered to the word it started on for the whole gesture
            (implicit pointer capture), the word under the finger is looked up
            by position instead of waiting for a `pointerenter` that never
            comes. Three phases of capture UX shipped before anyone tried it
            on a phone. */}
        <div
          className="select-none touch-none px-5 py-6 text-[16px] leading-8"
          onPointerMove={(e) => {
            if (e.pointerType === "mouse" || !dragging.current) return;
            const under = document.elementFromPoint(e.clientX, e.clientY);
            const index = under?.closest<HTMLElement>("[data-token-index]")?.dataset.tokenIndex;
            if (index !== undefined) extendTo(Number(index));
          }}
        >
          {tokens.map((token, i) =>
            token.isWord ? (
              <button
                key={i}
                type="button"
                data-token-index={i}
                aria-pressed={isSelected(i)}
                onPointerDown={(e) => {
                  dragging.current = true;
                  dragMoved.current = false;
                  // Shift+click extends: the press must not reset the anchor first.
                  if (e.shiftKey && dragStart !== null) {
                    setDragEnd(i);
                    return;
                  }
                  setDragStart(i);
                  setDragEnd(i);
                }}
                onPointerEnter={() => extendTo(i)}
                onPointerUp={() => {
                  dragging.current = false;
                }}
                onClick={(e) => {
                  // The click that closes a drag: the range is already set.
                  if (dragMoved.current) return;
                  if (e.shiftKey && dragStart !== null) {
                    setDragEnd(i);
                  } else {
                    setDragStart(i);
                    setDragEnd(i);
                  }
                }}
                className={cn(
                  "focus-ring cursor-pointer rounded-md px-0.5 font-[inherit] text-[inherit] leading-[inherit] transition-colors",
                  isSelected(i)
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-secondary",
                )}
              >
                {token.text}
              </button>
            ) : (
              <span key={i}>{token.text}</span>
            ),
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border bg-secondary/40 px-5 py-3">
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
              {t("chat.vocabularyPopup.selectionLabel")}
            </div>
            <p data-testid="vocabulary-popup-selection" className="truncate text-[13px] font-medium">
              {selectedText || (
                <span className="text-muted-foreground">
                  {t("chat.vocabularyPopup.noWordsSelected")}
                </span>
              )}
            </p>
            {tooLong && (
              <p className="text-[12px] text-destructive">{t("flashcards.captureErrors.selectionTooLong")}</p>
            )}
          </div>
          <button
            onClick={close}
            className="rounded-full px-4 py-2 text-[13px] font-medium text-muted-foreground hover:bg-background focus-ring"
          >
            {t("chat.vocabularyPopup.cancel")}
          </button>
          <button
            onClick={onSave}
            disabled={!selectedText || tooLong || !activeLanguage || loading}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all disabled:opacity-40 focus-ring"
          >
            {loading && <Loader2 className="size-3.5 animate-spin" />}
            {t("chat.vocabularyPopup.save")}
          </button>
        </div>
      </div>
      {/* Mounted only once the server has actually asked for it: the prompt
          reads the auth context, and this popup renders in surfaces that do
          not provide one. */}
      {askNative && <NativeLanguagePrompt open onOpenChange={setAskNative} status={l1Status} />}
    </div>
  );
}
